import { tokenStore } from "./tokens";
import type {
  ISession,
  IOtpRequestResponse,
  IUser,
  IParcel,
  ITicket,
  ITicketMessage,
  IPayResponse,
  IPaymentStatus,
  IConversation,
  IForwardingAddress,
  IFreighterRate,
  IOpsUser,
  IQuoteResponse,
  IQuoteRoute,
  ITransaction,
  IPaymentMetrics,
  IMarkPaidResponse,
  ILocation,
} from "./proto";

export type QuoteInput = {
  originCountry: string;
  mode: string;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
};

const API_HOST = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
export const API_BASE = `${API_HOST}/v1`;

const CONTENT_TYPE = "application/json";

export class APIError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message || code);
    this.status = status;
    this.code = code;
  }
}

let accessMem: string | null = null;

export async function currentAccess(): Promise<string | null> {
  if (accessMem) return accessMem;
  if (tokenStore.isNative) {
    accessMem = await tokenStore.getAccess();
  }
  return accessMem;
}

async function captureSession(s: ISession): Promise<ISession> {
  accessMem = s.accessToken ?? null;
  if (tokenStore.isNative && s.accessToken && s.refreshToken) {
    await tokenStore.setBoth(s.accessToken, s.refreshToken);
  }
  return s;
}

let refreshInFlight: Promise<boolean> | null = null;

export async function attemptRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const refresh = tokenStore.isNative ? await tokenStore.getRefresh() : null;
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: tokenStore.isNative ? "omit" : "include",
        headers: { "Content-Type": CONTENT_TYPE, Accept: CONTENT_TYPE },
        body: JSON.stringify(refresh ? { refreshToken: refresh } : {}),
      });
      if (!res.ok) return false;
      await captureSession((await res.json()) as ISession);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
  })();
  return refreshInFlight;
}

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown } = {},
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = { Accept: CONTENT_TYPE };
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = CONTENT_TYPE;
    body = JSON.stringify(opts.body);
  }
  const access = await currentAccess();
  if (access) headers["Authorization"] = `Bearer ${access}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    credentials: tokenStore.isNative ? "omit" : "include",
    headers,
    body,
  });

  if (res.status === 401 && !retried && !path.startsWith("/auth/")) {
    if (await attemptRefresh()) return request<T>(path, opts, true);
  }
  if (!res.ok) {
    let code = "error";
    let message = res.statusText;
    try {
      const err = await res.json();
      code = err.code || code;
      message = err.message || message;
    } catch { /* non-json error body */ }
    throw new APIError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export type RegisterInput = {
  trackingNumber?: string;
  expectedArrival?: string;
  originCountry: string;
  mode: string;
  description?: string;
  declaredValueKes?: number;
  labelCode?: string;
  deliveryMethod: string;
  deliveryArea?: string;
  deliveryAddress?: string;
  addressProof?: string;
};

export type DeliveryInput = {
  deliveryMethod: string;
  deliveryArea?: string;
  deliveryAddress?: string;
};

export type ProfileInput = {
  name?: string;
  locationArea?: string;
  town?: string;
  county?: string;
};

export type InvoiceInput = {
  freighterCost: number;
  chargeableWeightG?: number;
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
  category?: string;
  deliveryFeeKes: number;
  amountChargedKes: number;
};

export type ForwardingAddressInput = {
  originCountry?: string;
  mode?: string;
  shipTo?: string;
  notes?: string;
  warehouseEmail?: string;
  active?: boolean;
};

export type FreighterRateInput = {
  originCountry?: string;
  mode?: string;
  freighter?: string;
  costPerKgUsd?: string;
  markupPct?: string;
  volumetricDivisor?: number;
  fixedFeeUsd?: string;
  minChargeUsd?: string;
  pricePerM3Usd?: string;
  daysMin?: number;
  daysMax?: number;
  active?: boolean;
};

export type CreateOpsUserInput = { phone: string; role: string; name?: string };
export type UpdateOpsUserInput = { role?: string; active?: boolean };

export type OpsParcelQuery = { status?: string; archived?: boolean };

function opsParcelPath(q: OpsParcelQuery): string {
  const params = new URLSearchParams();
  if (q.status) params.set("status", q.status);
  if (q.archived) params.set("archived", "true");
  const qs = params.toString();
  return `/ops/parcels${qs ? `?${qs}` : ""}`;
}

export const api = {
  otpRequest: (phone: string) =>
    request<IOtpRequestResponse>("/auth/otp/request", { method: "POST", body: { phone } }),

  otpVerify: (phone: string, code: string) =>
    request<ISession>("/auth/otp/verify", { method: "POST", body: { phone, code } }).then(captureSession),

  passwordLogin: (phone: string, password: string) =>
    request<ISession>("/auth/password", { method: "POST", body: { phone, password } }).then(captureSession),

  logout: async () => {
    try {
      await request<void>("/auth/logout", { method: "POST" });
    } finally {
      accessMem = null;
      await tokenStore.clear();
    }
  },

  me: () => request<IUser>("/me"),

  setPassword: (password: string) =>
    request<void>("/auth/password/set", { method: "POST", body: { password } }),

  updateProfile: (p: ProfileInput) =>
    request<IUser>("/profile", { method: "PUT", body: p }),

  quote: (input: QuoteInput) => request<IQuoteResponse>("/quote", { method: "POST", body: input }),

  quoteRoutes: async (): Promise<IQuoteRoute[]> => {
    const r = await request<{ routes?: IQuoteRoute[] }>("/quote/routes");
    return r.routes ?? [];
  },

  registerPushToken: (token: string, platform = "web") =>
    request<void>("/push/tokens", { method: "POST", body: { token, platform } }),

  deletePushToken: (token: string) =>
    request<void>("/push/tokens", { method: "DELETE", body: { token } }),

  registerParcel: (p: RegisterInput) =>
    request<IParcel>("/parcels", { method: "POST", body: p }),

  uploadAddressProof: async (file: File): Promise<{ addressProof: string; url: string }> => {
    const form = new FormData();
    form.append("file", file);
    const access = await currentAccess();
    const res = await fetch(`${API_BASE}/parcels/address-proof`, {
      method: "POST",
      credentials: tokenStore.isNative ? "omit" : "include",
      headers: access ? { Authorization: `Bearer ${access}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      let code = "error";
      let message = res.statusText;
      try {
        const err = await res.json();
        code = err.code || code;
        message = err.message || message;
      } catch { /* non-json error body */ }
      throw new APIError(res.status, code, message);
    }
    return (await res.json()) as { addressProof: string; url: string };
  },

  addressProofUrl: (token: string) => `${API_BASE}/address-proof/${token}`,

  listParcels: async (archived = false): Promise<IParcel[]> => {
    const r = await request<{ parcels?: IParcel[] }>(`/parcels${archived ? "?archived=true" : ""}`);
    return r.parcels ?? [];
  },

  getParcel: (id: string) => request<IParcel>(`/parcels/${id}`),

  setDelivery: (id: string, d: DeliveryInput) =>
    request<IParcel>(`/parcels/${id}/delivery`, { method: "PUT", body: d }),

  locations: async (): Promise<ILocation[]> => {
    const r = await request<{ locations?: ILocation[] }>("/locations");
    return r.locations ?? [];
  },

  pay: (id: string, promptPhone?: string) =>
    request<IPayResponse>(`/parcels/${id}/pay`, { method: "POST", body: promptPhone ? { promptPhone } : {} }),

  payAll: (promptPhone?: string) =>
    request<IPayResponse>("/payments/pay-all", { method: "POST", body: promptPhone ? { promptPhone } : {} }),

  paymentStatus: (paymentId: string) => request<IPaymentStatus>(`/payments/${paymentId}`),

  listTickets: async (id: string): Promise<ITicket[]> => {
    const r = await request<{ tickets?: ITicket[] }>(`/parcels/${id}/tickets`);
    return r.tickets ?? [];
  },

  openTicket: (id: string, subject: string, body: string) =>
    request<ITicket>(`/parcels/${id}/tickets`, { method: "POST", body: { subject, body } }),

  myTickets: async (status?: string): Promise<ITicket[]> => {
    const r = await request<{ tickets?: ITicket[] }>(`/tickets${status ? `?status=${encodeURIComponent(status)}` : ""}`);
    return r.tickets ?? [];
  },

  unread: () =>
    request<{ ticket: Record<string, number>; parcel: Record<string, number> }>("/unread"),

  getTicket: (id: string, tid: string) => request<ITicket>(`/parcels/${id}/tickets/${tid}`),

  replyTicket: (id: string, tid: string, body: string) =>
    request<ITicketMessage>(`/parcels/${id}/tickets/${tid}/messages`, { method: "POST", body: { body } }),

  ops: {
    listParcels: async (q: OpsParcelQuery = {}): Promise<IParcel[]> => {
      const r = await request<{ parcels?: IParcel[] }>(opsParcelPath(q));
      return r.parcels ?? [];
    },

    transition: (id: string, action: OpsTransition) =>
      request<IParcel>(`/ops/parcels/${id}/${action}`, { method: "POST" }),

    receivedKenya: (id: string, i: InvoiceInput) =>
      request<IParcel>(`/ops/parcels/${id}/received-kenya`, { method: "POST", body: i }),

    hold: (id: string, body: { reason: string; amountKes?: number }) =>
      request<IParcel>(`/ops/parcels/${id}/hold`, { method: "POST", body }),

    addCharge: (id: string, body: { amountKes: number; reason: string }) =>
      request<IParcel>(`/ops/parcels/${id}/charge`, { method: "POST", body }),

    setDeliveryFee: (id: string, deliveryFeeKes: number) =>
      request<IParcel>(`/ops/parcels/${id}/delivery-fee`, { method: "POST", body: { deliveryFeeKes } }),

    openTicket: (id: string, subject: string, body: string) =>
      request<ITicket>(`/ops/parcels/${id}/tickets`, { method: "POST", body: { subject, body } }),

    replyTicket: (tid: string, body: string) =>
      request<ITicketMessage>(`/ops/tickets/${tid}/messages`, { method: "POST", body: { body } }),

    resolveTicket: (tid: string) =>
      request<void>(`/ops/tickets/${tid}/resolve`, { method: "POST" }),

    unread: () =>
      request<{ ticket: Record<string, number>; parcel: Record<string, number> }>("/ops/unread"),

    conversations: async (): Promise<IConversation[]> => {
      const r = await request<{ conversations?: IConversation[] }>("/ops/conversations");
      return r.conversations ?? [];
    },

    listForwardingAddresses: async (): Promise<IForwardingAddress[]> => {
      const r = await request<{ items?: IForwardingAddress[] }>("/ops/forwarding-addresses");
      return r.items ?? [];
    },
    createForwardingAddress: (a: ForwardingAddressInput) =>
      request<IForwardingAddress>("/ops/forwarding-addresses", { method: "POST", body: a }),
    updateForwardingAddress: (id: string, a: ForwardingAddressInput) =>
      request<IForwardingAddress>(`/ops/forwarding-addresses/${id}`, { method: "PUT", body: a }),
    deleteForwardingAddress: (id: string) =>
      request<void>(`/ops/forwarding-addresses/${id}`, { method: "DELETE" }),

    listFreighterRates: async (): Promise<IFreighterRate[]> => {
      const r = await request<{ items?: IFreighterRate[] }>("/ops/freighter-rates");
      return r.items ?? [];
    },
    createFreighterRate: (rate: FreighterRateInput) =>
      request<IFreighterRate>("/ops/freighter-rates", { method: "POST", body: rate }),
    updateFreighterRate: (id: string, rate: FreighterRateInput) =>
      request<IFreighterRate>(`/ops/freighter-rates/${id}`, { method: "PUT", body: rate }),
    deleteFreighterRate: (id: string) =>
      request<void>(`/ops/freighter-rates/${id}`, { method: "DELETE" }),

    getSettings: () => request<{ usdKesRate: number }>("/ops/settings"),
    setUsdKesRate: (usdKesRate: number) =>
      request<{ usdKesRate: number }>("/ops/settings", { method: "PUT", body: { usdKesRate } }),

    listUsers: async (): Promise<IOpsUser[]> => {
      const r = await request<{ items?: IOpsUser[] }>("/ops/users");
      return r.items ?? [];
    },
    createUser: (u: CreateOpsUserInput) =>
      request<IOpsUser>("/ops/users", { method: "POST", body: u }),
    updateUser: (id: string, u: UpdateOpsUserInput) =>
      request<void>(`/ops/users/${id}`, { method: "PUT", body: u }),

    listTransactions: async (q: TransactionQuery = {}): Promise<ITransaction[]> => {
      const r = await request<{ transactions?: ITransaction[] }>(transactionsPath(q));
      return r.transactions ?? [];
    },
    paymentMetrics: () => request<IPaymentMetrics>("/ops/transactions/metrics"),
    markPaid: (id: string) =>
      request<IMarkPaidResponse>(`/ops/parcels/${id}/mark-paid`, { method: "POST" }),

    promptPayment: (id: string, promptPhone?: string) =>
      request<IPayResponse>(`/ops/parcels/${id}/prompt`, { method: "POST", body: promptPhone ? { promptPhone } : {} }),
  },
};

export type TransactionQuery = { status?: string; q?: string; from?: string; to?: string; parcelId?: string };

function transactionsPath(q: TransactionQuery): string {
  const params = new URLSearchParams();
  if (q.parcelId) params.set("parcel_id", q.parcelId);
  if (q.status) params.set("status", q.status);
  if (q.q) params.set("q", q.q);
  if (q.from) params.set("from", q.from);
  if (q.to) params.set("to", q.to);
  const s = params.toString();
  return `/ops/transactions${s ? `?${s}` : ""}`;
}

export type OpsTransition = "receive" | "deliver" | "release" | "settle";
